import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

export type ChartImage = {
  exportId: string;
  title: string;
  caption: string;
  dataUrl: string;
};

export type StatusCardData = {
  count: string;
  status: string;
  colorScheme: string;
  label: string;
};

const FALLBACK_COLOR = { bg: "#E2E8F0", text: "#4A5568" };
const COLOR_MAP: Record<string, { bg: string; text: string }> = {
  orange: { bg: "#FEEBC8", text: "#C05621" },
  blue: { bg: "#BEE3F8", text: "#2B6CB0" },
  green: { bg: "#C6F6D5", text: "#276749" },
  purple: { bg: "#E9D8FD", text: "#6B46C1" },
  red: { bg: "#FED7D7", text: "#C53030" },
  yellow: { bg: "#FEFCBF", text: "#975A16" },
  teal: { bg: "#B2F5EA", text: "#285E61" },
  cyan: { bg: "#C4F1F9", text: "#0987A0" },
  pink: { bg: "#FED7E2", text: "#B83280" },
  gray: FALLBACK_COLOR,
};

function getColors(colorScheme: string): { bg: string; text: string } {
  return COLOR_MAP[colorScheme] ?? FALLBACK_COLOR;
}

export type DashboardPdfProps = {
  enrollmentPeriodName: string;
  formName: string;
  statusCards: StatusCardData[];
  chartImages: ChartImage[];
  hasSchoolSelectionSection: boolean;
};

const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: "#ffffff",
  },
  header: {
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
    paddingBottom: 12,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#2D3748",
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 11,
    color: "#718096",
  },
  statusSummary: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 20,
  },
  statusCard: {
    flexGrow: 1,
    flexBasis: 1,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 6,
    padding: 6,
    flexDirection: "column",
    gap: 4,
  },
  statusCount: {
    fontSize: 10,
    fontWeight: 600,
    color: "#2D3748",
  },
  statusTag: {
    alignSelf: "flex-start",
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  statusTagLabel: {
    fontSize: 6,
  },
  statusLabel: {
    fontSize: 6,
    color: "#718096",
  },
  chartsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  chartHalf: {
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 6,
    padding: 6,
    width: "48.5%",
  },
  chartFull: {
    display: "flex",
    flexDirection: "column",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 6,
    padding: 6,
    width: "100%",
  },
  chartTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: "#2D3748",
    marginBottom: 6,
  },
  chartImage: {
    width: "100%",
  },
  chartCaption: {
    fontSize: 6,
    color: "#718096",
    marginTop: "auto",
    textAlign: "center",
  },
});

const FULL_WIDTH_EXPORT_IDS = ["export-submissions-over-time"];

export const DashboardPdf: React.FC<DashboardPdfProps> = ({
  enrollmentPeriodName,
  formName,
  statusCards,
  chartImages,
  hasSchoolSelectionSection,
}) => {
  const getChartStyle = (exportId: string) => {
    if (FULL_WIDTH_EXPORT_IDS.includes(exportId)) {
      return styles.chartFull;
    }

    // When there's no school ranking section, the remaining charts
    // (response-by-status and responses-by-grade) should each take half width,
    // matching the two-column grid layout from the dashboard.
    return styles.chartHalf;
  };

  // When there's no school ranking section, reorder charts so that
  // response-by-status and responses-by-grade appear side by side first,
  // followed by full-width charts like submissions-over-time.
  const orderedChartImages = !hasSchoolSelectionSection
    ? [
        ...chartImages.filter(
          (c) => !FULL_WIDTH_EXPORT_IDS.includes(c.exportId)
        ),
        ...chartImages.filter((c) =>
          FULL_WIDTH_EXPORT_IDS.includes(c.exportId)
        ),
      ]
    : chartImages;

  return (
    <Document>
      <Page size="A4" orientation="portrait" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>
            {enrollmentPeriodName} — {formName}
          </Text>
        </View>

        {statusCards.length > 0 ? (
          <View style={styles.statusSummary}>
            {statusCards.map((card, idx) => {
              const colors = getColors(card.colorScheme);
              return (
                <View key={idx} style={styles.statusCard}>
                  <Text style={styles.statusCount}>{card.count}</Text>
                  <View
                    style={[styles.statusTag, { backgroundColor: colors.bg }]}
                  >
                    <Text
                      style={[styles.statusTagLabel, { color: colors.text }]}
                    >
                      {card.status}
                    </Text>
                  </View>
                  <Text style={[styles.statusLabel]}>{card.label}</Text>
                </View>
              );
            })}
          </View>
        ) : null}

        <View style={styles.chartsGrid}>
          {orderedChartImages.map((chart) => (
            <View
              key={chart.exportId}
              style={getChartStyle(chart.exportId)}
              wrap={false}
            >
              <Text style={styles.chartTitle}>{chart.title}</Text>
              <Image src={chart.dataUrl} style={styles.chartImage} />
              {chart.caption ? (
                <Text style={styles.chartCaption}>{chart.caption}</Text>
              ) : null}
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};
